<!-- Template for a new page -->
<template>
	<div class="d-flex flex-column flex h100">
		<navbar></navbar>
		<div class="app scroll-y">
			<div id="content" class="app-content box-shadow-3">
				<!-- Optional navbar at top of page -->
				<div class="navbar navbar-light bg-light b-b">
					<a class="navbar-brand flex">
						<span v-if="!refreshing">Scoring</span>
						<span v-if="refreshing">
							<loading type="inline-header" />
						</span>
					</a>
					<div class="checkbox mr-3">
						<label
							class="md-check"
							v-tippy
							title="Show cards for your pinned assessments even when they have no work to score"
						>
							<input v-model="showAll" type="checkbox" />
							<i class="theme-accent"></i>
							Show All Assessments
						</label>
					</div>
					<a class="btn-nav-topright b-l ml-0" href="/#/landing">
						<div>
							<i class="fas fa-th-large fa-fw" />
							Home
						</div>
					</a>
				</div>

				<div class="content scroll-y" style="width: 100vw;" v-if="!loading">
					<div class="padding pt-3 row flex-row-reverse m-0">
						<!-- Page content goes here -->
						<div class="col-12 col-lg-4 col-md-5 m-0">
							<scoring-recent-activity :size="10" :user="user"></scoring-recent-activity>
						</div>

						<div class="col-12 col-lg-8 col-md-7">
							<!-- Assessment list -->
							<div class="row mx-0 mb-4 align-items-center">
								<h3 class="flex m-0">My Assessments</h3>

								<div class="py-0">
									<button
										class="btn btn-sm theme-accent btn-block"
										data-toggle="modal"
										data-target="#addAssessmentsModal"
									>
										<i class="far fa-fw fa-plus"></i>
										<span class="hidden-folded d-inline">Add Assessments</span>
									</button>
								</div>
							</div>

							<div class="row">
								<!--Suggested work-->
								<div class="col-12">
									<h6 v-if="suggestions && suggestions.length" class="text-muted">Suggested Work</h6>
									<h6 v-if="!suggestions || !suggestions.length" class="text-muted">
										Suggested Work: None Available
										<i
											v-if="filteredAsmtStats.length"
											class="far fa-exclamation-circle"
											v-tippy
											:title="noSugTooltip1"
										/>
										<i
											v-if="!filteredAsmtStats.length"
											class="far fa-exclamation-circle"
											v-tippy
											:title="noSugTooltip2"
										/>
									</h6>
								</div>
								<assessment-row
									v-for="sug in suggestions"
									:key="sug.assessment_id"
									:assessment="sug.assessment"
									:rel="user.role.second_score && sug.rel"
									:res="user.role.resolution_score && sug.res"
									class="col-12"
									:class="{
										'text-extra-muted': !(
											(user.role.second_score && sug.rel) ||
											(user.role.resolution_score && sug.res)
										)
									}"
								>
									<template slot="actions">
										<button
											class="btn btn-block theme-accent text-white m-0"
											@click="pinSuggestion(sug)"
										>
											{{
												(user.role.second_score && sug.rel) ||
												(user.role.resolution_score && sug.res)
													? "Accept"
													: "Pin"
											}}
											<loading v-if="sug.assessment.loading" type="icon" />
										</button>
									</template>
								</assessment-row>

								<div
									v-if="assessmentStats.length && !showAll && !filteredAsmtStats.length"
									class="box section mx-auto mt-4 no-assignments"
								>
									<div class="box-header theme lighten-3">No Assessments</div>
									<div class="box-body light lt text-center p-4">
										<p class="m-0">
											None of your pinned assessments have any available work. Use the "Show All
											Assessments" checkbox above to show cards for assessments without work.
										</p>
									</div>
								</div>

								<div v-if="!assessmentStats.length" class="box section mx-auto mt-4 no-assignments">
									<div class="box-header theme lighten-3">No Assessments</div>
									<div class="box-body light lt text-center p-4">
										<p class="m-0">
											You have not picked any assessments to work on. Please use the "Add
											Assessments" button above, or add a suggested assessment from above.
										</p>
									</div>
								</div>

								<div
									v-if="filteredAsmtStats.length"
									class="col-xl-6 mt-3"
									v-for="stat in filteredAsmtStats"
									:key="stat.assessment_id"
								>
									<div class="box">
										<div class="box-body light lt p-0 d-flex flex-row align-items-center">
											<div class="img-square b-r">
												<img :src="stat.assessment.content_area.image_url" />
											</div>
											<div class="flex">
												<div class="d-flex flex-row align-items-center flex-wrap">
													<h6 class="flex px-3 py-2 mb-0">
														{{ stat.assessment.name }}
														<span v-if="stat.assessment.ref_id" class="mr-1">
															•
															<span class="text-muted text-sm">{{
																stat.assessment.ref_id
															}}</span>
														</span>
														<span class="flex nowrap">
															<span
																v-for="grade in stat.assessment.grades"
																:key="grade.id"
																class="badge theme-accent mr-1"
																>{{ grade.name }}</span
															>
														</span>
														<span class="nowrap nowrapgrades">
															<span
																v-for="(standard, i) in stat.assessment.standards"
																:key="i"
																class="badge theme-accent mr-1"
																>{{ standard }}</span
															>
														</span>
													</h6>
													<div
														class="b-l align-self-stretch d-flex flex-column justify-content-center"
														@click="unpinAssessment(stat.assessment)"
													>
														<div
															class="btn-round-subtle header-icon"
															v-tippy
															title="Unpin from My Assessments"
														>
															<i
																v-if="!stat.assessment.loading"
																class="fas fa-thumbtack"
															></i>
															<loading v-if="stat.assessment.loading" type="icon" />
														</div>
													</div>
												</div>
												<table class="table mb-0">
													<tbody>
														<tr v-for="item in stat.assessment.items" :key="item.id">
															<td class="v-mid b-r">
																<div class="text-muted">{{ item.name }}</div>
																<div v-if="stat.remaining" class="text-muted text-xs">
																	<span
																		v-if="user.role.second_score"
																		style="white-space: nowrap;"
																		v-tippy="{ placement: 'bottom' }"
																		title="Second scores remaining"
																	>
																		<i
																			class="fas fa-check-double mr-1 text-muted"
																		></i>
																		{{ stat.remaining.rel }}
																	</span>
																	<span
																		v-if="
																			user.role.second_score &&
																				user.role.resolution_score
																		"
																		class="mx-1"
																		>|</span
																	>
																	<span
																		v-if="user.role.resolution_score"
																		style="white-space: nowrap;"
																		v-tippy="{ placement: 'bottom' }"
																		title="Resolution scores remaining"
																	>
																		<i
																			class="fas fa-shield-check mr-1 text-muted"
																		></i>
																		{{ stat.remaining.res }}
																	</span>
																</div>
																<div v-if="!stat.remaining" class="text-muted text-xs">
																	<loading type="icon" />
																</div>
															</td>
															<td class="v-mid b-r" style="width: 1px;">
																<button
																	class="btn btn-icon btn-rounded theme text-white lighten-3"
																	data-toggle="dropdown"
																	v-tippy="{ delay: [500, 0] }"
																	title="Resources"
																	aria-expanded="false"
																>
																	<i class="fa fa-file-alt"></i>
																</button>
																<div class="dropdown-menu">
																	<div v-if="item.resources.length == 0">
																		<h6 class="text-muted text-center">
																			No Resources
																		</h6>
																	</div>
																	<template v-if="item.resources.length > 0">
																		<a
																			v-for="res in item.resources"
																			:key="res.id"
																			class="dropdown-item"
																			@click="viewResource(item, res)"
																		>
																			<i
																				class="far fa-lg faw"
																				:class="{
																					[fs.mimeTypeIcon(
																						res.mime_type
																					)]: true
																				}"
																			></i>
																			<label class="mb-0">{{ res.name }}</label>
																		</a>
																	</template>
																</div>
															</td>
															<td
																v-if="item.entries.length == 1"
																class="v-mid"
																style="width: 1px;"
															>
																<button
																	@click="item.entries[0].func(stat.assessment, item)"
																	class="btn btn-icon btn-rounded theme text-white button-pad"
																	v-tippy
																	:title="item.entries[0].name"
																>
																	<i
																		v-if="!item.loading"
																		class="fas"
																		:class="{ [item.entries[0].icon]: true }"
																	></i>
																	<loading v-if="item.loading" type="icon" />
																</button>
															</td>
															<td
																v-if="item.entries.length > 1"
																class="v-mid"
																style="width: 1px;"
															>
																<button
																	class="btn btn-icon btn-rounded theme text-white button-pad"
																	v-tippy
																	title="Score"
																	data-toggle="dropdown"
																	aria-expanded="false"
																>
																	<i
																		v-if="!item.loading"
																		class="fas fa-check"
																		style="margin-top: -2px;"
																	></i>
																	<i
																		v-if="!item.loading"
																		class="mt-2 fas fa-ellipsis-h"
																	></i>
																	<loading v-if="item.loading" type="icon" />
																</button>
																<div class="dropdown-menu dropdown-menu-right">
																	<a
																		v-for="(entry, i) in item.entries"
																		:key="i"
																		class="dropdown-item d-flex flex-row align-items-center"
																		@click="entry.func(stat.assessment, item)"
																	>
																		<button
																			class="btn btn-icon btn-rounded theme text-white button-pad"
																		>
																			<i
																				v-if="!item.loading"
																				class="dropdown-btn-icon fas"
																				:class="{ [entry.icon]: true }"
																			></i>
																			<loading v-if="item.loading" type="icon" />
																		</button>
																		<h6 class="ml-3 mb-1">{{ entry.name }}</h6>
																	</a>
																</div>
															</td>
														</tr>
													</tbody>
												</table>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="modal" id="addAssessmentsModal" data-backdrop="static" data-keyboard="false">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="mb-0">Add Assessments</h5>
					</div>
					<div class="modal-body modal-scroll">
						<div class="d-flex flex-row align-items-start">
							<div class="mr-2" style="width: 25%">
								<v-select
									multiple
									label="name"
									class="bg-white"
									:options="areas"
									v-model="sAreas"
								></v-select>
								<label class="text-muted ml-1">Content Area</label>
							</div>

							<div class="mr-2" style="width: 25%">
								<v-select
									multiple
									label="name"
									class="bg-white"
									:options="grades"
									v-model="sGrades"
								></v-select>
								<label class="text-muted ml-1">Grade</label>
							</div>

							<div style="width: 30%">
								<input type="text" class="form-control" v-model="newAsmtSearch" />
								<label class="text-muted ml-1">Name</label>
							</div>

							<div v-if="searchedAsmts" class="flex text-right">
								<h5 class="mb-0">{{ searchedTotalRows }} Results</h5>
								<div class="text-muted">({{ searchedAsmts.length }} shown)</div>
							</div>
						</div>

						<div style="height: 504px;">
							<h5
								v-if="!searchedAsmts || searchedAsmts.length == 0"
								class="text-very-muted text-center py-5"
							>
								Use the controls above to search
							</h5>
							<assessment-row
								v-if="searchedAsmts"
								v-for="asmt in searchedAsmts"
								:key="asmt.id"
								:assessment="asmt"
								:class="{ 'text-extra-muted': isPinned(asmt.id) }"
							>
								<template slot="actions">
									<button
										v-if="!isPinned(asmt.id)"
										class="btn btn-sm btn-icon btn-rounded theme text-white m-0"
										@click="pinAssessment(asmt)"
										v-tippy
										title="Add to My Assessments"
									>
										<i v-if="!asmt.loading" class="fas fa-thumbtack" />
										<loading v-if="asmt.loading" type="icon" />
									</button>
									<button
										v-if="isPinned(asmt.id)"
										class="btn btn-sm btn-icon btn-rounded theme text-white m-0"
										v-tippy
										title="Pinned"
									>
										<i v-if="!asmt.loading" class="fas fa-check" />
									</button>
								</template>
							</assessment-row>
						</div>
					</div>
					<div class="modal-footer">
						<button class="btn btn-flat theme" data-dismiss="modal">Done</button>
					</div>
				</div>
			</div>
		</div>

		<mzfooter></mzfooter>
	</div>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.nowrapgrades{
	white-space: normal !important ;
}
.no-assignments {
	max-width: 600px;
}
.section > .box-header {
	text-align: center;
	font-size: 28px;
}
.header-icon {
	font-size: 16px;
	opacity: 0.8;
	margin-right: 2px;
}
.button-pad {
	padding: 0.1rem;
}
.img-square {
	height: 95px;
	width: 95px;
}
.img-square > img {
	height: 95px;
	width: 95px;
	object-fit: cover;
	object-position: center top;
}
.dropdown-btn-icon {
	margin-left: 0.5rem;
	font-size: 1rem;
	opacity: 1;
}
</style>

<script>
//Libraries
import _ from "lodash";

//Services
import AuthService from "@/services/AuthService";
import ResourceService from "@/services/ResourceService";

import AssessmentService from "@/services/AssessmentService";
import ContentAreaService from "@/services/ContentAreaService";
import GradeService from "@/services/GradeService";

import AssessmentRow from "@/components/AssessmentRow";

import SecondScoreDriver from "@/services/ScoringDrivers/SecondScoreDriver";
import ResolutionDriver from "@/services/ScoringDrivers/ResolutionDriver";

import ScoringRecentActivity from "@/components/Scoring/ScoringRecentActivity";

import fs from "@/services/FormatService";
import Notie from "@/services/NotieService";
import Utils from "@/services/Utils";
import BB from "bluebird";
import $ from "jquery";

export default {
	name: "Scoring",

	components: {
		ScoringRecentActivity,
		AssessmentRow
	},

	data() {
		return {
			fs: fs,
			loading: true,
			refreshing: true,
			user: {},
			assessmentStats: [],
			filteredAsmtStats: [],
			suggestions: [],
			sections: [],
			section_stats: [],
			client: null,
			user: this.$route.meta.user,
			filter: "",
			showAll: false,

			areas: [],
			sAreas: null,
			grades: [],
			sGrades: null,

			newAsmtSearch: "",
			adding: false,
			searchedAsmts: null,
			searchedTotalRows: 0,

			noSugTooltip1: `<span class="text-md">You have pinned all assessments in your preferred Content Area(s) and Grade(s) that have available work.</span><br><br><small class="text-muted">You can use the Add Assessment button to search for assessments outside your preferred settings, or adjust your Profile to include more Content Areas and Grades.</small>`,
			noSugTooltip2: `<span class="text-md">There are no assessments in your preferred Content Area(s) and Grade(s) that have available work.</span><br><br><small class="text-muted">You can use the Add Assessment button to search for assessments outside your preferred settings, or adjust your Profile to include more Content Areas and Grades.</small>`
		};
	},

	watch: {
		newAsmtSearch() {
			if (this.loading) return;
			this.loadNewAsmts();
		},
		sAreas() {
			if (this.loading) return;
			this.loadNewAsmts();
		},
		sGrades() {
			if (this.loading) return;
			this.loadNewAsmts();
		},
		showAll() {
			this.filterAsmtStats();
		}
	},

	computed: {},

	created() {
		this.loadData();
	},
	methods: {
		loadData() {
			this.refreshing = true;
			BB.props({
				assessments: AssessmentService.listMyAssessments(),
				suggestions: AssessmentService.getSuggestedAssessments(3),
				contentAreas: ContentAreaService.listContentAreas(),
				grades: GradeService.listGrades()
			})
				.then(resps => {
					console.log(resps);
					this.assessmentStats = resps.assessments.data;
					var i;
					for (i = 0; i < this.assessmentStats.length; i++) {
						this.assessmentStats[i].assessment.items[0] = this.assessmentStats[i].assessment.item
						this.assessmentStats[i].assessment.items[0].assessment_id = this.assessmentStats[i].assessment.id
					}
					this.constructScoringEntries(this.assessmentStats);

					this.suggestions = resps.suggestions.data;

					this.areas = resps.contentAreas.data.content_areas;
					if (this.sAreas == null) {
						this.sAreas = [];
					}
					// _.each(this.user.content_area_ids, caid => {
					// 	let area = _.find(this.areas, { id: caid });
					// 	if (area) {
					// 		this.sAreas.push(area);
					// 	}
					// });

					this.grades = resps.grades.data;
					if (this.sGrades == null) {
						this.sGrades = [];
					}
					// _.each(this.user.grade_ids, gid => {
					// 	let grade = _.find(this.grades, { id: gid });
					// 	if (grade) {
					// 		this.sGrades.push(grade);
					// 	}
					// });

					this.loadRemainingResponses();

					this.loading = false;
				})
				.catch(err => {
					console.log(err);
					Notie.error("Failed to load assessments", err);
				})
				.finally(() => {
					this.refreshing = false;
				});
		},

		constructScoringEntries(stats) {
			_.each(stats, stat => {
				_.each(stat.assessment.items, item => {
					item.entries = [
						{
							name: "Second Score",
							func: this.enterSecondScore,
							icon: "fa-check-double"
						}
					];
					if (this.user.role.resolution_score) {
						item.entries.push({
							name: "Resolution",
							func: this.enterResolution,
							icon: "fa-shield-check"
						});
					}
				});
			});
		},

		loadNewAsmts() {
			let ctx = {
				active: true,
				filter: this.newAsmtSearch,
				currentPage: 1,
				perPage: 8
			};
			if (this.sAreas && this.sAreas.length) {
				ctx.cas = _.map(this.sAreas, "id");
			}
			if (this.sGrades && this.sGrades.length) {
				ctx.grades = _.map(this.sGrades, "id");
			}
			if (!this.newAsmtSearch) {
				ctx.suggest = true;
			}

			return AssessmentService.listAssessments(ctx).then(resp => {
				this.searchedAsmts = resp.data.assessments;
				this.searchedTotalRows = resp.data.totalRows;
			});
		},

		openNewAsmts() {
			this.adding = true;
			this.$nextTick(() => {
				$("#assessment-add")[0].scrollIntoView();
			});
		},

		loadRemainingResponses() {
			let ids = [];
			_.each(this.assessmentStats, stat => {
				ids.push(stat.assessment_id);
			});
			console.log("request", ids);
			AssessmentService.getRemainingResponses(ids).then(resp => {
				_.each(resp.data, rem => {
					_.each(this.assessmentStats, stat => {
						if (stat.assessment_id == rem.assessment_id) {
							stat.remaining = rem;
						}
					});
				});

				this.filterAsmtStats();
			});
		},

		filterAsmtStats() {
			let filteredAsmts = this.assessmentStats;
			if (!this.showAll) {
				filteredAsmts = _.filter(filteredAsmts, stat => {
					if (stat.remaining) {
						return stat.remaining.rel + stat.remaining.res > 0;
					} else {
						return false;
					}
				});
			}
			this.filteredAsmtStats = _.sortBy(filteredAsmts, [
				stat => {
					if (stat.remaining) {
						return -1 * (stat.remaining.rel + stat.remaining.res);
					} else {
						return 0;
					}
				}
			]);
		},

		pinSuggestion(sug) {
			this.pinAssessment(sug.assessment).then(() => {});
		},

		pinAssessment(asmt) {
			this.$set(asmt, "loading", true);
			return AssessmentService.pinAssessment(asmt.id)
				.then(resp => {
					Notie.info(`Added ${asmt.name} to My Assessments`);
					this.loadData();
				})
				.catch(err => {
					console.log(err);
					Notie.error("Failed to pin assessment", err);
					asmt.loading = false;
				});
		},

		unpinAssessment(asmt) {
			this.$set(asmt, "loading", true);
			return AssessmentService.unpinAssessment(asmt.id)
				.then(resp => {
					Notie.info(`Removed ${asmt.name} from My Assessments`);
					this.loadData();
				})
				.catch(err => {
					console.log(err);
					Notie.error("Failed to unpin assessment", err);
					asmt.loading = false;
				});
		},

		isPinned(id) {
			return _.find(this.assessmentStats, { assessment_id: id });
		},

		getCount(section_id, item_id) {
			var stat = _.find(this.section_stats, {
				section_id: section_id,
				item_id: item_id
			});
			if (stat) {
				return stat.count;
			}
			return 0;
		},

		getTime(section_id, item_id) {
			var stat = _.find(this.section_stats, {
				item_id: item_id,
				section_id: section_id
			});
			if (stat) {
				return Utils.getTimeString(stat.time);
			} else {
				return "00:00:00";
			}
		},

		isResolutionType(item) {
			if (item.type == "DOUBLE_EXACT" || item.type == "DOUBLE_WEIGHTED" || item.type == "OMAHA") {
				return true;
			}
			return false;
		},

		enterSecondScore(asmt, item) {
			item.loading_ss = true;
			this.$forceUpdate();
			SecondScoreDriver.getNext({
				assessment_id: asmt.id,
				item_id: item.id
			})
				.then(resp => {
					SecondScoreDriver.enter(this, asmt.id, item.id);
				})
				.catch(err => {
					Notie.error(`Cannot enter scoring for ${asmt.name}`, err);
				})
				.finally(() => {
					item.loading_ss = false;
					this.$forceUpdate();
				});
		},

		enterResolution(asmt, item) {
			item.loading_res = true;
			this.$forceUpdate();
			ResolutionDriver.getNext({
				assessment_id: asmt.id,
				item_id: item.id
			})
				.then(resp => {
					ResolutionDriver.enter(this, asmt.id, item.id);
				})
				.catch(err => {
					Notie.error(`Cannot enter resolution scoring for ${asmt.name}`, err);
				})
				.finally(() => {
					item.loading_res = false;
					this.$forceUpdate();
				});
		},

		enterTraining(item) {
			item.loading_res = true;
			Notie.error(`${item.name} - Enter training`, "Not yet implemented");
		},

		enterFlagQueue(section, item, alertID) {
			item.loading_flag = true;
			this.$forceUpdate();
			FlagQueueDriver.getNext({
				section_id: section.id,
				item_id: item.id,
				alert_id: alertID
			})
				.then(resp => {
					this.$router.push(`/flag_queue/${section.id}/${item.id}/${alertID}`);
				})
				.catch(err => {
					notie.error(`Cannot enter flag queue for ${item.name}`, err);
				})
				.finally(() => {
					item.loading_flag = false;
					this.$forceUpdate();
				});
		},

		goToTrainingSet(set) {
			TrainingDriver.getNext({
				item_id: set.item_id,
				section_id: set.section_id,
				set_id: set.id
			})
				.then(resp => {
					this.$router.push(`/training/${set.section_id}/${set.item_id}/${set.id}`);
				})
				.catch(e => {
					this.$router.push(`/qc_set_report/${set.id}`);
				});
		},

		goToRfSet(set) {
			RFDriver.getNext({
				item_id: set.item_id,
				section_id: set.section_id,
				set_id: set.id
			})
				.then(resp => {
					this.$router.push(`/rangefinding/${set.section_id}/${set.item_id}/${set.id}`);
				})
				.catch(e => {
					this.$router.push(`/rf_set_report/${set.id}`);
				});
		},

		viewResource(item, resource) {
			console.log("Getting resource from item ")
			if (resource.type == 1) {
				ResourceService.viewResource(item.assessment_id, resource);
			} else if (resource.type == 2) {
				ResourceService.viewExternalResource(resource.path);
			}
		},

		available(project) {
			return ProjectService.checkIfAvailable(project, this.client.timezone);
		}
	}
};
</script>



// WEBPACK FOOTER //
// src/vues/Scoring/Scoring.vue